import { useEffect, useRef, useState } from "react";
import { Download, Share2, Instagram, Music2, Home, RotateCw } from "lucide-react";
import { toast } from "sonner";

/**
 * ShareCard — composes per-platform share PNGs and hands them to the
 * native OS share sheet on mobile.
 *
 *   4:5  (1080 x 1350) — Instagram feed
 *   9:16 (1080 x 1920) — TikTok / Instagram Story
 *
 * MOBILE SHARE STRATEGY (the honest one):
 *   Browsers cannot inject a file directly into a third-party app via a
 *   deep link (`instagram://`, `tiktok://`). The ONLY reliable way to hand
 *   a generated image from web → native app on iOS/Android is
 *   `navigator.share({ files: [png] })`, which raises the OS share sheet
 *   with the image already attached. The user then taps Instagram or
 *   TikTok in the sheet — the app opens with the image ready to post.
 *
 *   So on mobile we call navigator.share directly on the click gesture,
 *   no pre-emptive window.open, no fallbacks that swallow the sheet.
 *   Desktop (no share support) gets a download + web upload page.
 */
export default function ShareCard({ score, onClose, onRestart, onExitToLanding }) {
  const previewCanvasRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);

  const caption = `${score} clouds climbed. Sign up for early access. nuvolaclo.com`;

  const isMobile = typeof navigator !== "undefined" &&
    /android|iphone|ipad|ipod/i.test(navigator.userAgent || "");
  const canShareFiles = typeof navigator !== "undefined" &&
    typeof navigator.canShare === "function";

  // Image cache so we don't reload per render
  const imgsRef = useRef(null);
  const loadImages = async () => {
    if (imgsRef.current) return imgsRef.current;
    const load = (src) => new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = src;
    });
    const [bg, mark, wordmark] = await Promise.all([
      load("/assets/share_bg.jpg"),
      load("/assets/nuvola_w.png"),
      load("/assets/nuvola_word.png"),
    ]);
    imgsRef.current = { bg, mark, wordmark };
    return imgsRef.current;
  };

  // Render the card into the given canvas at the chosen ratio
  const render = async (cv, ratio = "4:5") => {
    if (!cv) return;
    const ctx = cv.getContext("2d");
    const W = 1080;
    const H = ratio === "9:16" ? 1920 : 1350;
    cv.width = W;
    cv.height = H;

    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, W, H);

    try { await document.fonts.ready; } catch (_) { /* noop */ }
    const { bg, mark, wordmark } = await loadImages();

    if (bg) {
      // Source is now a native 9:16 image (1080x1920). The 9:16 canvas
      // fills exactly 1-to-1 with no scaling and no bars. The 4:5 canvas
      // crops the top & bottom of the source via a vertical-cover map.
      const srcAspect = bg.width / bg.height;
      const viewAspect = W / H;
      let dw, dh;
      if (viewAspect < srcAspect) {
        // 4:5 case → image is taller than view → match width, crop top/bottom
        dw = W;
        dh = W / srcAspect;
      } else {
        // 9:16 case → aspects match → fills exactly
        dh = H;
        dw = H * srcAspect;
      }
      const dx = Math.round((W - dw) / 2);
      const dy = Math.round((H - dh) / 2);
      ctx.drawImage(bg, dx, dy, dw, dh);
    }

    // Soft edge vignette
    const vg = ctx.createRadialGradient(W/2, H*0.5, Math.min(W,H)*0.45, W/2, H*0.5, Math.max(W,H)*0.95);
    vg.addColorStop(0, "rgba(0,0,0,0)");
    vg.addColorStop(1, "rgba(0,0,0,0.7)");
    ctx.fillStyle = vg;
    ctx.fillRect(0, 0, W, H);

    // ----- Top band: W mark -----
    const topY = ratio === "9:16" ? 160 : 110;
    if (mark) {
      const mw = 130;
      const mh = mw * (mark.height / mark.width);
      ctx.globalAlpha = 0.95;
      ctx.drawImage(mark, (W - mw) / 2, topY, mw, mh);
      ctx.globalAlpha = 1;
    }

    // ----- Label -----
    const labelY = topY + 230;
    ctx.fillStyle = "rgba(255,255,255,0.6)";
    ctx.font = "400 22px 'IBM Plex Mono', monospace";
    ctx.textAlign = "center";
    const spaced = (s) => s.toUpperCase().split("").join(" ");
    ctx.fillText(spaced("clouds climbed"), W / 2, labelY);

    // ----- Huge score number -----
    const scoreY = labelY + 220;
    ctx.fillStyle = "#ffffff";
    ctx.font = "italic 300 280px 'Cormorant Garamond', 'Times New Roman', serif";
    ctx.fillText(String(score).padStart(2, "0"), W / 2, scoreY);

    // ----- Bottom band: wordmark only (no URL) -----
    const wordY = H - (ratio === "9:16" ? 240 : 170);
    if (wordmark) {
      const ww = 360;
      const wh = ww * (wordmark.height / wordmark.width);
      const tmp = document.createElement("canvas");
      tmp.width = ww; tmp.height = wh;
      const tctx = tmp.getContext("2d");
      tctx.drawImage(wordmark, 0, 0, ww, wh);
      tctx.globalCompositeOperation = "source-in";
      tctx.fillStyle = "rgba(255,255,255,0.95)";
      tctx.fillRect(0, 0, ww, wh);
      ctx.drawImage(tmp, (W - ww) / 2, wordY);
    }

    // ----- Film grain overlay -----
    const grainCv = document.createElement("canvas");
    grainCv.width = W; grainCv.height = H;
    const gctx = grainCv.getContext("2d");
    const grain = gctx.createImageData(W, H);
    for (let i = 0; i < grain.data.length; i += 4) {
      const v = (Math.random() * 255) | 0;
      grain.data[i] = v; grain.data[i+1] = v; grain.data[i+2] = v; grain.data[i+3] = 255;
    }
    gctx.putImageData(grain, 0, 0);
    ctx.save();
    ctx.globalAlpha = 0.06;
    ctx.globalCompositeOperation = "overlay";
    ctx.drawImage(grainCv, 0, 0);
    ctx.restore();
  };

  useEffect(() => {
    (async () => {
      await render(previewCanvasRef.current, "9:16");
      setReady(true);
    })();
  }, [score]);

  const composeBlob = async (ratio) => {
    const off = document.createElement("canvas");
    await render(off, ratio);
    return await new Promise((r) => off.toBlob(r, "image/png"));
  };

  const triggerDownload = (b, filename) => {
    const url = URL.createObjectURL(b);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  };

  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text); return true; }
    catch (_) { return false; }
  };

  /**
   * FLOW A — the native OS share sheet with the image + caption attached.
   *
   *   On iOS Safari and Android Chrome, `navigator.share({ files })` is the
   *   ONLY sanctioned path that physically moves a file from a web page
   *   into Instagram or TikTok. The player taps our button → the OS share
   *   sheet slides up with the 9:16 PNG as a visible preview and the
   *   caption underneath → they tap the Instagram or TikTok icon in that
   *   sheet → the app opens on its own composer with the image already
   *   loaded. Deep-linking an app URL scheme skips the sheet but also
   *   loses the file — the previous "Flow B" attempt proved that in the
   *   wild and has been retired.
   *
   *   The blob is composed synchronously inside the click handler so iOS
   *   Safari counts navigator.share as a user-gesture call.
   */
  const shareNative = async ({ ratio, filename, webFallback }) => {
    if (busy) return;
    setBusy(true);
    try {
      const b = await composeBlob(ratio);
      if (!b) return;
      const file = new File([b], filename, { type: "image/png" });

      if (canShareFiles && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], text: caption });
          return; // share sheet handled everything
        } catch (err) {
          // User tapped cancel → leave quietly.
          if (err && err.name && (err.name === "AbortError" || err.name === "NotAllowedError")) {
            return;
          }
          // Any other failure → fall through to the safety net below.
        }
      }

      // Safety net — desktop, or the rare mobile browser without file-share
      // support. Save the image + copy the caption + open the web upload.
      triggerDownload(b, filename);
      await copyText(caption);
      if (webFallback) {
        window.open(webFallback, "_blank", "noopener,noreferrer");
      }
      toast("image saved · caption copied");
    } finally {
      setBusy(false);
    }
  };

  const onInstagram = () => shareNative({
    ratio: "9:16",
    filename: `nuvola-${score}-story.png`,
    webFallback: "https://www.instagram.com/",
  });

  const onTikTok = () => shareNative({
    ratio: "9:16",
    filename: `nuvola-${score}-tiktok.png`,
    webFallback: "https://www.tiktok.com/",
  });

  const onSave = async () => {
    const b = await composeBlob("9:16");
    if (b) triggerDownload(b, `nuvola-${score}.png`);
  };

  // "more" also uses the native share sheet — same as Instagram/TikTok but
  // without an implied destination so the player can pick anywhere.
  const onMore = () => shareNative({
    ratio: "9:16",
    filename: `nuvola-${score}.png`,
    webFallback: null,
  });

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-lg flex items-center justify-center p-4 sm:p-6 overflow-y-auto"
      data-testid="share-card-overlay"
    >
      <div className="max-w-sm w-full my-auto">
        <div className="relative w-full aspect-[9/16] mb-5 overflow-hidden bg-black">
          <canvas
            ref={previewCanvasRef}
            className="absolute inset-0 w-full h-full"
            data-testid="share-canvas"
          />
          {!ready && (
            <div className="absolute inset-0 flex items-center justify-center text-white/40 font-mono text-[10px] tracking-[0.4em] uppercase">
              composing…
            </div>
          )}
        </div>

        <p className="text-center font-mono text-[10px] tracking-[0.3em] uppercase text-white/40 mb-4">
          share the fall
        </p>

        {/* Primary platform buttons */}
        <div className="grid grid-cols-2 gap-2 mb-2">
          <button
            onClick={onInstagram}
            disabled={busy}
            className="flex items-center justify-center gap-2 px-3 py-3 bg-white text-black font-mono text-[10px] tracking-[0.3em] uppercase hover:bg-white/85 transition-colors duration-300 rounded-sm disabled:opacity-50"
            data-testid="share-instagram"
          >
            <Instagram size={15} />
            <span>instagram</span>
          </button>
          <button
            onClick={onTikTok}
            disabled={busy}
            className="flex items-center justify-center gap-2 px-3 py-3 bg-white text-black font-mono text-[10px] tracking-[0.3em] uppercase hover:bg-white/85 transition-colors duration-300 rounded-sm disabled:opacity-50"
            data-testid="share-tiktok"
          >
            <Music2 size={15} />
            <span>tiktok</span>
          </button>
        </div>

        {/* Helper line — Flow A: share sheet opens with image already attached */}
        {isMobile && (
          <p className="text-center font-mono text-[9px] tracking-[0.25em] uppercase text-white/35 mb-4 leading-relaxed">
            tap · pick instagram or tiktok<br/>· image is already attached
          </p>
        )}
        {!isMobile && (
          <p className="text-center font-mono text-[9px] tracking-[0.25em] uppercase text-white/35 mb-4 leading-relaxed">
            image + caption ready to upload
          </p>
        )}

        <div className="grid grid-cols-2 gap-2 mb-4">
          <button
            onClick={onSave}
            disabled={busy}
            className="flex items-center justify-center gap-2 px-3 py-2.5 border border-white/15 text-white/90 font-mono text-[10px] tracking-[0.3em] uppercase hover:bg-white hover:text-black transition-colors duration-300 rounded-sm disabled:opacity-50"
            data-testid="share-download"
          >
            <Download size={14} />
            <span>save</span>
          </button>
          <button
            onClick={onMore}
            disabled={busy}
            className="flex items-center justify-center gap-2 px-3 py-2.5 border border-white/15 text-white/90 font-mono text-[10px] tracking-[0.3em] uppercase hover:bg-white hover:text-black transition-colors duration-300 rounded-sm disabled:opacity-50"
            data-testid="share-more"
          >
            <Share2 size={14} />
            <span>more</span>
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-4">
          <button
            onClick={onRestart}
            disabled={busy}
            className="flex items-center justify-center gap-2 px-3 py-2.5 border border-white/70 text-white font-mono text-[10px] tracking-[0.3em] uppercase hover:bg-white hover:text-black transition-colors duration-300 rounded-sm disabled:opacity-50"
            data-testid="share-play-again"
          >
            <RotateCw size={14} />
            <span>play again</span>
          </button>
          <button
            onClick={onExitToLanding}
            disabled={busy}
            className="flex items-center justify-center gap-2 px-3 py-2.5 border border-white/70 text-white font-mono text-[10px] tracking-[0.3em] uppercase hover:bg-white hover:text-black transition-colors duration-300 rounded-sm disabled:opacity-50"
            data-testid="share-home"
          >
            <Home size={14} />
            <span>home</span>
          </button>
        </div>

        <button
          onClick={onClose}
          className="block w-full text-center font-mono text-[10px] tracking-[0.35em] uppercase text-white/50 hover:text-white py-2"
          data-testid="share-close"
        >
          close
        </button>
      </div>
    </div>
  );
}
